import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Phone, Mail, MessageSquare, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollmentsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enrollments`);
      setRows(data);
    } catch (e) {
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this enrollment?")) return;
    try {
      await axios.delete(`${API}/admin/enrollments/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const trialCount = rows.filter((r) => r.trial).length;

  return (
    <div data-testid="enrollments-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-bold text-brand-ink">Enrollments</h2>
          <p className="text-brand-ink/70">{rows.length} total · {trialCount} trial bookings</p>
        </div>
        <button onClick={load} className="rounded-full border-2 border-brand-ink bg-white px-4 py-1.5 font-bold text-sm hover:bg-brand-yellow" data-testid="enrollments-refresh">Refresh</button>
      </div>

      <div className="mt-6 rounded-3xl border-[3px] border-brand-ink bg-white shadow-brutal-sm overflow-hidden">
        {loading && <p className="p-6 text-brand-ink/60">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="p-8 text-center text-brand-ink/60">No enrollments yet. Parents can submit through the website.</p>
        )}
        {!loading && rows.length > 0 && (
          <div className="divide-y-2 divide-brand-ink/10">
            {rows.map((r) => (
              <div key={r.id} className="p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-testid={`enrollment-row-${r.id}`}>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-xl font-semibold">{r.child_name} <span className="text-brand-ink/50 text-sm">({r.child_age})</span></p>
                    {r.trial && (
                      <span className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-brand-green text-white px-2.5 py-0.5 text-xs font-bold">
                        <Sparkles size={12} /> Trial booking
                      </span>
                    )}
                  </div>
                  <p className="text-brand-ink/70 mt-0.5">Parent: <strong>{r.parent_name}</strong></p>
                  <div className="mt-2 flex flex-wrap gap-3 text-sm">
                    <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-brand-red font-semibold"><Phone size={14} /> {r.phone}</a>
                    {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-brand-ink"><Mail size={14} /> {r.email}</a>}
                  </div>
                  {r.message && (
                    <p className="mt-2 text-sm text-brand-ink/80 bg-brand-cream border-2 border-brand-ink/20 rounded-xl p-3 inline-flex items-start gap-2">
                      <MessageSquare size={14} className="mt-0.5 shrink-0" /> <span>{r.message}</span>
                    </p>
                  )}
                  <p className="mt-2 text-xs text-brand-ink/50">Submitted {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <button
                  onClick={() => remove(r.id)}
                  data-testid={`enrollment-delete-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-white px-3 py-1.5 text-sm font-bold text-brand-red hover:bg-brand-red hover:text-white transition-colors self-start"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
